import { useState, useCallback } from 'react';
import { ref, get, push, set, update, remove } from 'firebase/database';
import { database } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useAppDataContext } from '../../context/AppDataContext';
import { useCart } from '../../context/CartContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from '../common/Icon';

export default function StaffHomeTab({
  clockedIn,
  clockInTime,
  today,
  todayStr,
  tasks,
  todaySalesOrders,
  onClockIn,
  onClockOut,
  onShowStockCheck,
  onShowIntake,
  onShowPOS,
  onShowPettyCash,
  onShowReceiving,
  onNavigateQuiz,
}) {
  const { user } = useAuth();
  const { tasks: globalTasks, shipments } = useAppDataContext();
  const { cartCount } = useCart();
  const { t } = useTranslation();

  // Local state — typing here no longer re-renders parent or sibling tabs
  const [newMemoText, setNewMemoText] = useState('');

  // Direct Firebase write for tasks — bypasses SyncContext to avoid full-array overwrite
  const pushTaskToFirebase = useCallback(async (taskData) => {
    try {
      const taskRef = push(ref(database, 'tasks'));
      await set(taskRef, taskData);
    } catch (err) {
      console.error('Failed to write task to Firebase:', err);
    }
  }, []);

  const toggleTaskInFirebase = useCallback(async (taskId, newDone) => {
    try {
      const tasksSnap = await get(ref(database, 'tasks'));
      if (!tasksSnap.exists()) return;
      const data = tasksSnap.val();
      const targetKey = Object.entries(data).find(([, v]) => v && v.id === taskId)?.[0];
      if (!targetKey) return;
      if (newDone) {
        await update(ref(database, '/'), {
          [`tasks/${targetKey}/done`]: true,
          [`tasks/${targetKey}/completedAt`]: new Date().toISOString(),
        });
      } else {
        await update(ref(database, `tasks/${targetKey}`), { done: false });
        await remove(ref(database, `tasks/${targetKey}/completedAt`));
      }
    } catch (err) {
      console.error('Failed to toggle task in Firebase:', err);
    }
  }, []);

  // Derived data for messages section
  const adminTasks = globalTasks.filter(tk => {
    if (tk.date && tk.date !== todayStr) return false;
    return tk.target === user.id;
  });
  const myMessages = globalTasks.filter(tk => {
    if (tk.date && tk.date !== todayStr) return false;
    return tk.from === user.id && tk.target === 'admin';
  });
  const allItems = [...adminTasks, ...myMessages].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const receivingTasks = globalTasks.filter(tk =>
    tk.type === 'receiving' && tk.shipmentId && tk.target === user.id && !tk.done
  );

  const sendMemo = () => {
    if (!newMemoText.trim()) return;
    pushTaskToFirebase({
      id: Date.now(),
      text: newMemoText.trim(),
      done: false,
      date: todayStr,
      createdAt: new Date().toISOString(),
      from: user.id,
      fromName: user.name,
      target: 'admin',
    });
    setNewMemoText('');
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Clock in status */}
      {!clockedIn ? (
        <button onClick={onClockIn} className="w-full bg-gradient-to-r from-gold-600 to-gold-500 rounded-2xl p-6 text-center">
          <div className="text-4xl mb-2">{'\uD83D\uDFE2'}</div>
          <div className="text-dark-900 font-bold text-lg">{t('staff.clockIn')}</div>
          <div className="text-dark-900/70 text-sm">{today.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</div>
        </button>
      ) : (
        <div className="bg-t-card border border-t-border rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse mr-3"></div>
              <div>
                <div className="text-t-primary font-medium">{t('staff.working')}</div>
                <div className="text-t-secondary text-sm">{clockInTime} {t('staff.since')}</div>
              </div>
            </div>
            <button onClick={onClockOut} className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm flex items-center gap-1"><span>{'\uD83D\uDC4B'}</span>{t('staff.clockOut')}</button>
          </div>
        </div>
      )}

      {/* Today's tasks */}
      <div className="bg-t-card border border-t-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-t-border flex items-center justify-between">
          <span className="text-t-primary font-medium">{t('staff.todayTasks')}</span>
          <span className="text-t-accent text-sm">{tasks.completed}/{tasks.total}</span>
        </div>
        <div className="divide-y divide-t-border">
          {tasks.list.map(task => (
            <div
              key={task.id}
              className={`px-4 py-3 flex items-center ${task.clickable ? 'cursor-pointer hover:bg-t-card-hover active:bg-t-card-hover' : ''} ${task.locked ? 'opacity-50' : ''}`}
              onClick={() => {
                if (task.id === 'check' && task.clickable) onShowStockCheck();
                if (task.id === 'quiz' && task.clickable) onNavigateQuiz();
              }}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 ${task.done ? 'bg-green-500' : 'bg-t-input'}`}>
                {task.done && <Icon name="Check" size={14} className="text-t-primary" />}
              </div>
              <span className={`flex-1 ${task.done ? 'text-t-secondary line-through' : task.locked ? 'text-t-secondary' : 'text-t-primary'}`}>{task.name}</span>
              {task.locked && <span className="text-t-secondary text-xs mr-2">{t('stockCheck.locked')}</span>}
              {task.clickable && <Icon name="ChevronRight" size={16} className="text-t-accent mr-2" />}
              <span className="text-t-accent text-sm">+{task.points}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Today's results */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-t-card border border-t-border rounded-xl p-4">
          <div className="text-t-secondary text-xs mb-1">{t('staff.today')}</div>
          <div className="text-2xl font-bold text-t-primary">¥{todaySalesOrders.reduce((s, o) => s + (o.total || 0), 0).toLocaleString()}</div>
          <div className="text-t-secondary text-xs">{todaySalesOrders.length}{t('staff.items')}</div>
        </div>
        <div className="bg-t-card border border-t-border rounded-xl p-4">
          <div className="text-t-secondary text-xs mb-1">{t('staff.earnedPoints')}</div>
          <div className="text-2xl font-bold text-t-accent">{tasks.list.filter(tk => tk.done).reduce((s, tk) => s + tk.points, 0)}</div>
          <div className="text-t-secondary text-xs">pts</div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-3 gap-2">
        <button onClick={onShowIntake} className="bg-t-card border border-t-border rounded-xl p-3 flex flex-col items-center hover:border-gold-500/50 transition active:scale-95">
          <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center mb-1">
            <Icon name="Plus" size={20} className="text-green-500" />
          </div>
          <span className="text-t-primary text-xs">{t('staff.stockIn')}</span>
        </button>
        <button onClick={onShowPOS} className="bg-t-card border border-t-border rounded-xl p-3 flex flex-col items-center hover:border-gold-500/50 transition active:scale-95 relative">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center mb-1">
            <Icon name="ShoppingBag" size={20} className="text-blue-500" />
          </div>
          <span className="text-t-primary text-xs">{t('staff.newSale')}</span>
          {cartCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-t-primary text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">{cartCount}</span>
          )}
        </button>
        <button onClick={onShowPettyCash} className="bg-t-card border border-t-border rounded-xl p-3 flex flex-col items-center hover:border-gold-500/50 transition active:scale-95">
          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center mb-1">
            <Icon name="Wallet" size={20} className="text-amber-500" />
          </div>
          <span className="text-t-primary text-xs">{t('staff.pettyCash')}</span>
        </button>
      </div>

      {/* Messages & Tasks from Admin */}
      <div className="bg-t-card border border-t-border rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-t-border flex items-center justify-between">
          <span className="text-t-primary font-medium flex items-center gap-2">
            <Icon name="MessageSquare" size={16} className="text-t-accent" />
            {t('staff.memo')}
          </span>
          {adminTasks.filter(tk => !tk.done).length > 0 && (
            <span className="px-1.5 py-0.5 bg-red-500 text-t-primary text-xs rounded-full">
              {adminTasks.filter(tk => !tk.done).length}
            </span>
          )}
        </div>

        {/* Send message to admin */}
        <div className="p-3 border-b border-t-border">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMemoText}
              onChange={e => setNewMemoText(e.target.value)}
              placeholder={t('staff.sendToAdmin')}
              className="flex-1 bg-t-input border border-t-border rounded-lg px-3 py-2 text-t-primary text-sm placeholder-gray-500"
              onKeyPress={e => { if (e.key === 'Enter') sendMemo(); }}
            />
            <button
              onClick={sendMemo}
              className="px-3 py-2 bg-t-accent text-dark-900 rounded-lg font-medium text-sm"
            >
              <Icon name="Send" size={16} />
            </button>
          </div>
        </div>

        {/* Task & message list */}
        <div className="max-h-56 overflow-y-auto divide-y divide-t-border">
          {allItems.length === 0 ? (
            <div className="p-4 text-center text-t-secondary text-sm">{t('staff.noMemos')}</div>
          ) : allItems.slice(0, 8).map(item => {
            const isFromAdmin = item.target === user.id;
            const isMyMsg = item.from === user.id;
            return (
              <div key={item.id} className={`px-4 py-3 ${isFromAdmin && !item.done ? 'bg-blue-500/5' : ''}`}>
                <div className="flex items-start gap-3">
                  {isFromAdmin && (
                    <button
                      onClick={() => toggleTaskInFirebase(item.id, !item.done)}
                      className={`w-5 h-5 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition ${item.done ? 'bg-green-500 border-green-500' : 'border-blue-500 hover:border-blue-400'}`}
                    >
                      {item.done && <Icon name="Check" size={12} className="text-t-primary" />}
                    </button>
                  )}
                  {isMyMsg && (
                    <div className="w-5 h-5 mt-0.5 rounded-full bg-t-accent-soft flex-shrink-0 flex items-center justify-center">
                      <Icon name="ArrowUp" size={12} className="text-t-accent" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm ${item.done ? 'text-t-secondary line-through' : 'text-t-primary'}`}>
                      {item.text}
                    </div>
                    <div className="text-xs mt-0.5">
                      {isFromAdmin ? (
                        <span className="text-blue-400">← {item.fromName || t('staff.fromAdmin')}</span>
                      ) : (
                        <span className="text-t-secondary">→ {t('staff.toAdmin')} · {new Date(item.createdAt).toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Receiving tasks (from shipments) */}
          {receivingTasks.map(tk => {
            const ship = (shipments || []).find(s => s.id === tk.shipmentId);
            if (!ship || ship.status !== 'pending') return null;
            return (
              <div key={`recv-${tk.id}`} className="px-4 py-3 bg-emerald-500/5">
                <button
                  onClick={() => onShowReceiving(ship)}
                  className="w-full flex items-start gap-3 text-left"
                >
                  <div className="w-5 h-5 mt-0.5 rounded-full bg-emerald-500/20 flex-shrink-0 flex items-center justify-center">
                    <Icon name="Truck" size={12} className="text-emerald-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-t-primary">{tk.text}</div>
                    <div className="text-xs text-emerald-400 mt-0.5">
                      {t('shipment.itemCount', { count: ship.items?.length || 0 })} · タップして検品開始 →
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
